import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, BUTTON_THEME, Icon, Input, Select } from '@st-operations/uikit/src/v2'
import Dropzone from 'react-dropzone'
import papaParse from 'papaparse'
import * as crypto from 'crypto'
import union from 'lodash.union'

import { ConfirmPrompt } from '_components/confirm-prompt'
import { buildSegmentIdOptions, getSegmentIdName } from './segment-id-options'

// Constants
import geoCodes from '_constants/geos.json'
import stateCodes from '_constants/us_states.json'

// Styles
import './styles.scss'

// lib
import pretty from '@st-operations/uikit/lib/pretty.js'
import getCountryName from '_lib/getCountryName'
import User from '_lib/User'
import downloadCSV from '_lib/downloadCSV'

// services
import { getTaxonomyCoverage } from '_services/api-integrations'
import { getMappingIds } from '_services/taxonomies'
import { getTaxonomyIdSets } from '_services/taxonomy-id-sets'

import DistributionCoverage from '../api-integrations/coverage/distribution-coverage'
import { PERCENTILE_TAXONOMY_NAME } from '../constants'

// a saved taxonomy's id - a new one has none yet, so nothing to cover
const OBJECT_ID = /^[a-f0-9]{24}$/i

// an override as the coverage rows and the form both hold it - '' for none
const overrideKey = (value) => (value === null || value === undefined ? '' : String(value))

const INITIAL_DISTRIBUTION = {
  destination: '',
  geo: [],
  exclude_subiso: [],
  whitelist: '',
  blocklist: '',
  prefix: '',
  lookback: 'audience',
  mapping_ids: '',
  predictor_percentile_override: null,
}

const GEO_OPTIONS = geoCodes.map((geo) => ({ value: geo.value, label: geo.label }))

const STATES_OPTIONS = Object.values(stateCodes).map((state) => ({
  value: state.code,
  label: `${state.name} (${state.code})`,
}))

const PASTE_MAP = {
  geo: GEO_OPTIONS,
  exclude_subiso: STATES_OPTIONS,
}

const Distribution = ({
  distributions,
  setError,
  handleChange,
  slug,
  needSegmentIds,
  taxonomyId,
  taxonomyName,
}) => {
  const [deletedDisIndex, setDeletedDisIndex] = useState(null)
  const [isConfirmDeleteModalOpen, setConfirmDeleteModal] = useState(false)
  const [segment_destinations, setDestinations] = useState([])
  const [downloadingIndex, setDownloadingIndex] = useState(null)

  // Taxonomy ID sets, managed under Segment Ids and offered to every taxonomy.
  // Each occupies its own mapping space on an audience, so they join the
  // built-in spaces in this dropdown rather than replacing any of them.
  const [customIdSets, setCustomIdSets] = useState([])

  const segmentIdOptions = buildSegmentIdOptions(customIdSets)
  const segmentIdName = (code) => getSegmentIdName(customIdSets, code)

  // How much of this taxonomy each partner its distributions go to has -
  // worked out from the partners' nightly lists when the page opens. Shown
  // under the rows it belongs to; a row edited since the last save matches
  // none until it is saved.
  const [coverage, setCoverage] = useState(null)
  const [coverageError, setCoverageError] = useState(null)

  useEffect(() => {
    if (!OBJECT_ID.test(taxonomyId || '')) return undefined
    let cancelled = false
    setCoverage(null)
    setCoverageError(null)
    getTaxonomyCoverage(taxonomyId)
      .then((data) => {
        if (!cancelled) setCoverage(data?.items || [])
      })
      .catch((e) => {
        if (!cancelled) setCoverageError(e?.message || 'could not work it out')
      })
    return () => {
      cancelled = true
    }
  }, [taxonomyId])

  const coverageFor = (distribution) =>
    (coverage || []).find(
      (row) =>
        row.mapping_id === distribution.mapping_ids &&
        overrideKey(row.predictor_percentile_override) ===
          overrideKey(distribution.predictor_percentile_override) &&
        row.destinations.some((d) => d.slug === distribution.destination)
    ) || null

  const createSegmentId = (name, slug) => {
    const code = name.replace(/\s>\s/g, '/').replace(/\s/g, '-').replace(/,/g, '').toLowerCase()
    const country = 'global'
    const s = `sharethis_${country}/${code}/${slug}`
    return crypto.createHash('md5').update(s).digest('hex').slice(0, 8)
  }

  const confirmDeleteDistribution = () => {
    if (deletedDisIndex !== null && typeof deletedDisIndex === 'number') {
      let newDistributions = distributions
      newDistributions.splice(deletedDisIndex, 1)
      setDeletedDisIndex(null)
      setConfirmDeleteModal(false)
      handleChange({ name: 'distribution' }, newDistributions)
    }
  }

  const cancelRemoveDistribution = () => {
    setConfirmDeleteModal(false)
    setDeletedDisIndex(null)
  }

  const removeDistribution = (index) => {
    setConfirmDeleteModal(true)
    setDeletedDisIndex(index)
  }

  const addNewDistribution = () => {
    let newDistributions = distributions
    newDistributions.push({ ...INITIAL_DISTRIBUTION })
    handleChange({ name: 'distribution' }, newDistributions)
  }

  const handleInputUpdateDistribution = (value, name, index) => {
    const distribution = distributions[index]
    distribution[name] = value

    handleChange({ name: 'distribution' }, distributions)
  }

  const handleSelectUpdateDistribution = (option, name, value, index) => {
    const distribution = distributions[index]
    distribution[name] = value
    if (name === 'geo') {
      if (value.length === 1) {
        if (value[0] === 'GLOBAL' || !value[0]) {
          distribution.prefix = 'ShareThis'
        } else {
          distribution.prefix = `ShareThis > ${value[0]}`
        }
      } else {
        distribution.prefix = 'ShareThis > Custom'
      }
    }

    if (name === 'destination') {
      if (value) {
        const matchedSegmentDestinations = segment_destinations.filter((sd) => sd.value === value)
        if (matchedSegmentDestinations.length === 0) {
          distribution['new_destination'] = true
          let destination = value
            .replace(/\s>\s/g, '/')
            .replace(/\s/g, '-')
            .replace(/,/g, '')
            .toLowerCase()

          distribution['name'] = value
          distribution['destination'] = destination
        }
      }
    }

    handleChange({ name: 'distribution' }, distributions)
  }

  const loadSegmentDestinations = () => {
    window._dmp_api.get('/v1.0/segment-destinations', (error, data) => {
      if (data) {
        let segments = data.segment_destinations.map((des) => ({
          value: des.destination,
          label: des.destination,
        }))
        setDestinations(segments)
      }
      if (error) {
        setError(error)
      }
    })
  }

  const loadCustomIdSets = () =>
    getTaxonomyIdSets()
      .then((data) => setCustomIdSets(data?.items ?? []))
      .catch(() => {
        // a failed load costs the custom entries in the dropdown, nothing else -
        // the built-in spaces and the rest of the row still work
        setCustomIdSets([])
      })

  useEffect(() => {
    User.watch(this, (user) => {
      if (user) {
        loadSegmentDestinations()
        loadCustomIdSets()
      }
    })
  }, [])

  const addCountryCodesToDistribution = (distribution) => {
    const codes = geoCodes
      .filter((f) => f.value !== 'GLOBAL')
      .sort((s1, s2) => (s1.label > s2.label ? 1 : -1))
      .map((geo) => geo.value)

    distribution.geo = union(distribution.geo, codes)

    handleChange({ name: 'distribution' }, distributions)
  }

  const addStateCodesToDistribution = (distribution) => {
    const codes = STATES_OPTIONS.map((state) => state.value)
    distribution.exclude_subiso = codes
    handleChange({ name: 'distribution' }, distributions)
  }

  const renderSelectFieldValue = (distribution, field) => {
    const potential_value = distribution[field.name]
    if (Array.isArray(potential_value)) {
      if (['geo'].includes(field.name)) {
        return potential_value.map((code) => ({ label: getCountryName(code) || code, value: code }))
      }
    } else if (field.name === 'mapping_ids') {
      return {
        value: potential_value,
        label: segmentIdName(potential_value) ?? pretty(potential_value),
      }
    } else {
      return { value: potential_value, label: pretty(potential_value) }
    }
  }

  const renderInputFieldValue = (distribution, field) => {
    return distribution[field.name] ?? ''
  }

  const onDropAudienceFile = (files) => {
    const file = files?.[0]
    if (file?.type !== 'text/csv') {
      return toast.error('File is invalid. Please upload a csv file')
    }
    const reader = new FileReader()
    reader.readAsText(file)
    reader.onload = (event) => {
      const segments = []
      const csv = event.target.result.concat('\n')
      const rows = papaParse.parse(csv)
      const name_seen = []
      const id_seen = []
      rows.data.forEach((row) => {
        const new_segment = {}
        if (needSegmentIds) {
          new_segment['path'] = row[0]
          new_segment['id'] = createSegmentId(new_segment.path, slug)
        } else {
          if (row.length < 2) {
            return toast.error('Missing id column. Please make sure to put ids in the first column')
          }
          new_segment['path'] = row[1]
          new_segment['id'] = row[0]
        }

        if (!name_seen.includes(new_segment.path) && !id_seen.includes(new_segment.id)) {
          id_seen.push(new_segment.id)
          name_seen.push(new_segment.path)
          segments.push(new_segment)
        }
      })

      handleChange({ name: 'segments' }, segments)
      toast.success('Segments successfully added. Click Save in order to create/add new audiences')
    }
  }

  const handlePaste = (disIndex, field) => (e) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text/plain')
    const values = pastedText
      .split(/[,\n]/)
      .map((v) => v.trim().toUpperCase())
      .filter((v) => PASTE_MAP[field].some((option) => option.value === v))

    if (values.length) {
      const distribution = distributions[disIndex]
      const newValues = union(distribution[field] || [], values)
      handleSelectUpdateDistribution(null, field, newValues, disIndex)
    }
  }

  const getDestinationLabel = (destination) =>
    segment_destinations.find((sd) => sd.value === destination)?.label ?? pretty(destination)

  const getSegmentIdLabel = (mappingIds) => segmentIdName(mappingIds) ?? pretty(mappingIds)

  const buildMappingIdsFilename = (distribution) => {
    const { destination, mapping_ids, predictor_percentile_override } = distribution
    const date = new Date().toISOString().split('T')[0]
    const parts = [
      'Mapping IDs',
      taxonomyName,
      getDestinationLabel(destination),
      getSegmentIdLabel(mapping_ids),
      ...(predictor_percentile_override != null
        ? [`override ${predictor_percentile_override}`]
        : []),
      date,
    ]
    return parts.join(' - ')
  }

  const handleDownloadMappingIds = (distribution, index) => () => {
    if (downloadingIndex !== null) {
      return
    }

    const { mapping_ids, predictor_percentile_override } = distribution

    setDownloadingIndex(index)
    getMappingIds(taxonomyId, { mapping_ids, predictor_percentile_override })
      .then((rows) => {
        if (!Array.isArray(rows) || rows.length === 0) {
          toast.error('No mapping IDs found for this distribution')
          return
        }
        const fields = Object.keys(rows[0])
        const csv = papaParse.unparse({ fields, data: rows })
        downloadCSV(csv, buildMappingIdsFilename(distribution))
        toast.success('Mapping IDs downloaded successfully')
      })
      .catch((error) => {
        toast.error(error?.message || 'Failed to download mapping IDs')
      })
      .finally(() => {
        setDownloadingIndex(null)
      })
  }

  const renderDistributions = () => {
    if (distributions.length > 0) {
      return distributions.map((distribution, index) => {
        const disIndex = index
        return (
          <div key={index} className="distributions-container">
            <div className="dis-info">
              <div className="distributions__button-select-wrapper">
                <Select
                  key="destination"
                  name="destination"
                  label="Destination (DSP/DMP)"
                  // on the right of its label row, where the other fields keep
                  // their hints, so they line up with the fields below
                  description={
                    <span className="distributions__actions">
                      <button
                        type="button"
                        className="distributions__action"
                        onClick={handleDownloadMappingIds(distribution, disIndex)}
                        disabled={downloadingIndex !== null}
                      >
                        <Icon
                          width="12px"
                          height="12px"
                          icon="download"
                          type="fill"
                          color="#ffffff"
                        />
                        {downloadingIndex === disIndex ? 'Downloading...' : 'Download Mapping IDs'}
                      </button>
                      <button
                        type="button"
                        className="distributions__action distributions__action--remove"
                        onClick={() => removeDistribution(disIndex)}
                      >
                        <Icon
                          width="10px"
                          height="10px"
                          icon="delete"
                          type="fill"
                          color="#ff2710"
                        />
                        Remove
                      </button>
                    </span>
                  }
                  value={
                    distribution.destination
                      ? { value: distribution.destination, label: pretty(distribution.destination) }
                      : null
                  }
                  onChange={(option, name, val) =>
                    handleSelectUpdateDistribution(option, name, val, disIndex)
                  }
                  options={segment_destinations}
                  useReactSelect
                  style={{ gridColumn: '1 / -1' }}
                />
              </div>
              <div className="distributions__fields-wrapper">
                {/* a read-only field of its own, the first row, across the grid */}
                <If condition={coverageFor(distribution)}>
                  <div style={{ gridColumn: '1/-1' }}>
                    <DistributionCoverage
                      row={coverageFor(distribution)}
                      taxonomy={{ _id: taxonomyId, name: taxonomyName, slug }}
                    />
                  </div>
                </If>
                <Select
                  key="mapping_ids"
                  name="mapping_ids"
                  label="Segment IDs"
                  description=""
                  style={{ gridColumn: '1/2' }}
                  options={segmentIdOptions}
                  onChange={(option, name, val) =>
                    handleSelectUpdateDistribution(option, name, val, disIndex)
                  }
                  value={renderSelectFieldValue(distribution, { name: 'mapping_ids' })}
                  useReactSelect={true}
                  isSearchable={true}
                />
                <Select
                  key="lookback"
                  name="lookback"
                  label="Lookback"
                  description=""
                  style={{ gridColumn: '2/3' }}
                  options={[
                    { value: 'audience', label: 'audience' },
                    { value: '1-day', label: '1-day' },
                    { value: '30-day', label: '30-day' },
                  ]}
                  onChange={(option, name, val) =>
                    handleSelectUpdateDistribution(option, name, val, disIndex)
                  }
                  value={renderSelectFieldValue(distribution, { name: 'lookback' })}
                  useReactSelect={true}
                />
                <Select
                  key="ethnicity"
                  name="ethnicity"
                  label="Ethnicity"
                  description=""
                  style={{ gridColumn: '3/4' }}
                  options={[
                    { value: 'ALL', label: 'ALL' },
                    { value: 'Hispanic', label: 'Hispanic' },
                  ]}
                  onChange={(option, name, val) =>
                    handleSelectUpdateDistribution(option, name, val, disIndex)
                  }
                  value={renderSelectFieldValue(distribution, { name: 'ethnicity' })}
                  useReactSelect={true}
                />
                {taxonomyName === PERCENTILE_TAXONOMY_NAME && (
                  <Input
                    key="predictor_percentile_override"
                    name="predictor_percentile_override"
                    label="Predictor Percentile Override"
                    value={
                      distribution.predictor_percentile_override != null
                        ? String(distribution.predictor_percentile_override)
                        : ''
                    }
                    containerStyle={{ gridColumn: '4/6' }}
                    type="number"
                    onChange={(value, name) => {
                      const parsed =
                        value === '' ? null : Math.min(99, Math.max(0, parseInt(value, 10)))
                      handleInputUpdateDistribution(isNaN(parsed) ? null : parsed, name, disIndex)
                    }}
                  />
                )}
                <Input
                  key="whitelist"
                  name="whitelist"
                  label="Segment ID Whitelist"
                  value={renderInputFieldValue(distribution, { name: 'whitelist' })}
                  description="Comma Delineated List of IDs"
                  containerStyle={{ gridColumn: '1/4' }}
                  onChange={(value, name) => handleInputUpdateDistribution(value, name, disIndex)}
                />
                <Input
                  key="blocklist"
                  name="blocklist"
                  label="Segment ID Blocklist"
                  value={renderInputFieldValue(distribution, { name: 'blocklist' })}
                  description="Comma Delineated List of IDs"
                  containerStyle={{ gridColumn: '4/-1' }}
                  onChange={(value, name) => handleInputUpdateDistribution(value, name, disIndex)}
                />
                <div onPaste={handlePaste(disIndex, 'geo')} style={{ gridColumn: '1/-1' }}>
                  <Select
                    key="geo"
                    name="geo"
                    label={
                      <span style={{ fontSize: '14px', fontWeight: '600' }}>
                        Geo Allow List
                        <span
                          className="rounded-md p-1 px-2 ml-3 w-fit font-semibold text-sm bg-primary text-gray-100 cursor-pointer"
                          onClick={() => addCountryCodesToDistribution(distribution)}
                        >
                          Add All Countries
                        </span>
                      </span>
                    }
                    description="Tip: You can paste multiple country codes separated by commas or new lines"
                    options={GEO_OPTIONS}
                    onChange={(option, name, val) =>
                      handleSelectUpdateDistribution(option, name, val, disIndex)
                    }
                    value={renderSelectFieldValue(distribution, { name: 'geo' })}
                    useReactSelect={true}
                    isSearchable={true}
                    isMulti={true}
                  />
                </div>
                <div
                  onPaste={handlePaste(disIndex, 'exclude_subiso')}
                  style={{ gridColumn: '1/-1' }}
                >
                  <Select
                    key="exclude_subiso"
                    name="exclude_subiso"
                    label={
                      <span style={{ fontSize: '14px', fontWeight: '600' }}>
                        US State Blocklist
                        <span
                          className="rounded-md p-1 px-2 ml-3 w-fit font-semibold text-sm bg-primary text-gray-100 cursor-pointer"
                          onClick={() => addStateCodesToDistribution(distribution)}
                        >
                          Add All States
                        </span>
                      </span>
                    }
                    description="Tip: You can paste multiple state codes separated by commas or new lines"
                    options={STATES_OPTIONS}
                    onChange={(option, name, val) =>
                      handleSelectUpdateDistribution(option, name, val, disIndex)
                    }
                    value={(distribution.exclude_subiso || []).map((code) => ({
                      value: code,
                      label: `${stateCodes[code]?.name} - ${code}`,
                    }))}
                    useReactSelect={true}
                    isSearchable={true}
                    isMulti={true}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      })
    }
    return null
  }

  return (
    <div className="distributions">
      <div className="distributions-header">
        <h3 className="title">Distribution</h3>
        <Button theme={BUTTON_THEME.PRIMARY} onClick={addNewDistribution}>
          Add New Distribution
        </Button>
      </div>
      <div className="audience-dnd">
        <Dropzone multiple={false} onDrop={onDropAudienceFile}>
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps({ className: 'dropzone' })}>
              <div className="dnd-container">
                <div>
                  <Icon width="35px" height="40px" color="#13682B" type="fill" icon="download" />
                </div>
                <div className="info">
                  <span style={{ fontSize: '14px', lineHeight: '24px', color: '#3D3D3D' }}>
                    Drop or <b>click</b> to upload new audiences
                  </span>
                  <span style={{ fontSize: '12px', lineHeight: '18px', color: '#666666' }}>
                    Make sure your file is in .CSV format
                  </span>
                </div>
              </div>
            </div>
          )}
        </Dropzone>
      </div>
      <If condition={OBJECT_ID.test(taxonomyId || '') && !coverage && !coverageError}>
        <div className="distribution-coverage-note">Checking partner coverage…</div>
      </If>
      <If condition={coverageError}>
        <div className="distribution-coverage-note is-bad">
          Couldn&apos;t work out partner coverage - {coverageError}
        </div>
      </If>
      {renderDistributions()}
      <ConfirmPrompt
        title="Remove Distribution"
        description="This distribution will be removed. You can't undo this action."
        okText="Yes"
        cancelText="No"
        isOpen={isConfirmDeleteModalOpen}
        onCancel={cancelRemoveDistribution}
        onOK={confirmDeleteDistribution}
      />
    </div>
  )
}

export default Distribution

Distribution.defaultProps = {
  segmentsConfig: {},
  needSegmentIds: false,
}

Distribution.propTypes = {
  distributions: PropTypes.arrayOf(
    PropTypes.shape({
      destination: PropTypes.string,
      prefix: PropTypes.string,
      mapping_ids: PropTypes.string,
      lookback: PropTypes.string,
      geo: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  setError: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  slug: PropTypes.string.isRequired,
  needSegmentIds: PropTypes.bool,
  taxonomyId: PropTypes.string,
  taxonomyName: PropTypes.string,
}
